import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../services/api';
import { fetchAll } from '../services/apiHelpers';

/**
 * Receipt Store - إدارة سندات القبض المستقلة
 *
 * NEW INDEPENDENT RECEIPT SYSTEM (December 2025)
 * - Receipts are NO LONGER linked to specific invoices
 * - They reduce total customer debt
 * - Have sequential numbers (RCP-YYYYMMDD-000001)
 *
 * Protocols Applied:
 * - P-STATE-001: Zustand State Management
 * - P034: Data Validation
 * - P010: Error Handling
 * - P025: Cache Management (LocalStorage persist)
 */

const receiptsAPI = {
  getAll: () => api.get('/mobile/receipts'),
  getById: (id) => api.get(`/mobile/receipts/${id}`),
  create: (data) => api.post('/mobile/receipts', data),
  update: (id, data) => api.put(`/mobile/receipts/${id}`, data),
  delete: (id) => api.delete(`/mobile/receipts/${id}`),
  search: (query) => api.get(`/mobile/receipts/search?query=${encodeURIComponent(query)}`),
  getByCustomer: (customerId) => api.get(`/mobile/receipts/customer/${customerId}`)
};

const useReceiptStore = create(
  persist(
    (set, get) => ({
      // State
      receipts: [],
      loading: false,
      error: null,

      // Actions

      /**
       * Fetch all receipts from API
       */
      fetchReceipts: async () => {
        return await fetchAll(receiptsAPI.getAll, set, get, 'receipts');
      },

      /**
       * Add new receipt
       * @param {Object} receipt - Receipt data
       */
      addReceipt: async (receipt) => {
        try {
          set({ error: null, loading: true });

          const response = await receiptsAPI.create(receipt);
          const newReceipt = response.data.data || response.data;

          const state = get();
          set({
            receipts: [newReceipt, ...state.receipts],
            loading: false
          });

          return newReceipt;
        } catch (error) {
          const errorMessage = error.response?.data?.message || error.message;
          set({ error: errorMessage, loading: false });
          throw error;
        }
      },

      /**
       * Update existing receipt
       * @param {number} id - Receipt ID
       * @param {Object} updates - Fields to update
       */
      updateReceipt: async (id, updates) => {
        try {
          set({ error: null, loading: true });

          await receiptsAPI.update(id, updates);

          const state = get();
          set({
            receipts: state.receipts.map((r) =>
              r.id === id ? { ...r, ...updates, updated_at: new Date().toISOString() } : r
            ),
            loading: false
          });
        } catch (error) {
          const errorMessage = error.response?.data?.message || error.message;
          set({ error: errorMessage, loading: false });
          throw error;
        }
      },

      /**
       * Delete receipt
       * @param {number} id - Receipt ID
       */
      deleteReceipt: async (id) => {
        try {
          set({ error: null, loading: true });

          await receiptsAPI.delete(id);

          const state = get();
          set({
            receipts: state.receipts.filter((r) => r.id !== id),
            loading: false
          });
        } catch (error) {
          const errorMessage = error.response?.data?.message || error.message;
          set({ error: errorMessage, loading: false });
          throw error;
        }
      },

      /**
       * Get receipt by ID
       * @param {number} id - Receipt ID
       * @returns {Object|null} Receipt object or null
       */
      getReceiptById: (id) => {
        return get().receipts.find((receipt) => receipt.id === id) || null;
      },

      /**
       * Search receipts by receipt number or customer name
       * @param {string} query - Search query
       * @returns {Array} Matching receipts
       */
      searchReceipts: (query) => {
        if (!query || !query.trim()) return get().receipts;

        const lowerQuery = query.toLowerCase().trim();
        return get().receipts.filter(
          (receipt) =>
            (receipt.receipt_number && receipt.receipt_number.toLowerCase().includes(lowerQuery)) ||
            (receipt.customer?.name && receipt.customer.name.toLowerCase().includes(lowerQuery)) ||
            (receipt.notes && receipt.notes.toLowerCase().includes(lowerQuery))
        );
      },

      /**
       * Get receipts by customer ID
       * @param {number} customerId - Customer ID
       * @returns {Array} Customer receipts
       */
      getReceiptsByCustomer: (customerId) => {
        return get().receipts.filter((receipt) => receipt.customer_id === customerId);
      },

      /**
       * Get total receipts amount
       * @returns {number} Total amount
       */
      getTotalReceiptsAmount: () => {
        return get().receipts.reduce((total, receipt) => {
          return total + parseFloat(receipt.amount || 0);
        }, 0);
      },

      /**
       * Get receipts count
       * @returns {number} Total receipts
       */
      getTotalReceipts: () => {
        return get().receipts.length;
      },

      /**
       * Clear error
       */
      clearError: () => set({ error: null })
    }),
    {
      name: 'receipt-storage', // LocalStorage key
      partialize: (state) => ({ receipts: state.receipts }) // Only persist receipts
    }
  )
);

export default useReceiptStore;
